import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import schoolList from '../../data/school_list.json';
import SchoolNameUtils from '../../utils/schoolNameUtils';
import { Search, X } from 'lucide-react';

const SchoolSearchInput = ({ 
  value, 
  onChange, 
  onSchoolSelect,
  placeholder = "학교명을 검색하세요",
  disabled = false,
  className = "",
  required = false,
  isWorkbook = false,
  onWorkbookChange,
  isSearchOnly = false,  // 검색 전용 모드
  forceInputType = null   // 외부에서 강제로 inputType 설정
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [inputType, setInputType] = useState(forceInputType || (isWorkbook ? 'workbook' : 'school'));
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // forceInputType이 변경될 때 inputType 업데이트
  useEffect(() => {
    if (forceInputType) {
      setInputType(forceInputType);
    }
  }, [forceInputType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) &&
          dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 드롭다운 위치 업데이트
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [showSuggestions]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSelectedIndex(-1);

    if (inputType === 'school') {
      // 학교 모드에서는 검색만 가능, 직접 입력 불가
      if (!isSearchOnly) {
        // 객체 생성/수정 모달에서는 입력값으로 필터링만 하고 onChange 호출 안함
        if (newValue.trim()) {
          const filtered = SchoolNameUtils.filterSchools(schoolList, newValue);
          setFilteredSchools(filtered.slice(0, 10)); // 최대 10개만 표시
          setShowSuggestions(filtered.length > 0);
        } else {
          // 입력값이 비어있으면 전체 목록 표시
          setFilteredSchools(schoolList.slice(0, 10));
          setShowSuggestions(true);
        }
        // onChange 호출하지 않음 - 드롭다운에서 선택해야만 값 변경
      } else {
        // 검색 모드에서는 자유 입력 가능
        if (newValue.trim()) {
          const filtered = SchoolNameUtils.filterSchools(schoolList, newValue);
          setFilteredSchools(filtered.slice(0, 10));
          setShowSuggestions(filtered.length > 0);
        } else {
          setFilteredSchools([]);
          setShowSuggestions(false);
        }
        if (onChange) {
          onChange(newValue);
        }
      }
    } else {
      // 문제집 모드
      if (onWorkbookChange) {
        onWorkbookChange(newValue);
      }
    }
  };

  const handleSchoolSelect = (school) => {
    const abbreviation = SchoolNameUtils.getAbbreviation(school.school_name, school.school_level);
    setInputValue(school.school_name);
    setShowSuggestions(false);
    
    if (onSchoolSelect) {
      onSchoolSelect(school, abbreviation);
    }
    if (onChange) {
      onChange(school.school_name);
    }
  };

  const handleKeyDown = (e) => {
    if (inputType === 'workbook') {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        // 문제집 입력 시 Enter 키로 추가
        if (onSchoolSelect) {
          onSchoolSelect(inputValue.trim());
        }
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < filteredSchools.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredSchools.length) {
        handleSchoolSelect(filteredSchools[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  const handleClear = () => {
    setInputValue('');
    setFilteredSchools([]);
    setShowSuggestions(false);
    if (onChange) {
      onChange('');
    }
    if (inputType === 'workbook' && onWorkbookChange) {
      onWorkbookChange('');
    }
    inputRef.current?.focus();
  };

  const handleTypeChange = (type) => {
    setInputType(type);
    setInputValue('');
    setFilteredSchools([]);
    setShowSuggestions(false);
    if (onChange) {
      onChange('');
    }
    // 학교 모드로 전환 시 전체 학교 목록 표시
    if (type === 'school' && !isSearchOnly) {
      setFilteredSchools(schoolList.slice(0, 10));
      setShowSuggestions(true);
    }
    inputRef.current?.focus();
  };

  const validateInput = () => {
    if (isSearchOnly) return true;  // 검색 모드에서는 유효성 검사 안함
    if (inputType === 'school' && inputValue && !SchoolNameUtils.isValidSchool(inputValue, schoolList)) {
      return false;
    }
    return true;
  };

  return (
    <div className="w-full">
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          {/* 검색 전용 모드가 아닐 때만 버튼 표시 */}
          {!isSearchOnly && (
            <div className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10">
              <div className="flex bg-gray-200 rounded-full p-[1px]">
                <button
                  type="button"
                  onClick={() => handleTypeChange('school')}
                  className={`px-2.5 py-0.5 text-xs rounded-full transition-all duration-200 ${
                    inputType === 'school'
                      ? 'bg-white text-[#4ABC85] shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  학교
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeChange('workbook')}
                  className={`px-2.5 py-0.5 text-xs rounded-full transition-all duration-200 ${
                    inputType === 'workbook'
                      ? 'bg-white text-[#4ABC85] shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  문제집
                </button>
              </div>
            </div>
          )}
          
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputType === 'school') {
                if (!isSearchOnly) {
                  // 객체 생성/수정 모달에서는 클릭 시 전체 목록 표시
                  if (!inputValue.trim()) {
                    setFilteredSchools(schoolList.slice(0, 10));
                  }
                  setShowSuggestions(true);
                } else if (filteredSchools.length > 0) {
                  setShowSuggestions(true);
                }
              }
            }}
            placeholder={isSearchOnly ? "학교명 검색" : (inputType === 'school' ? "학교를 선택하세요" : "문제집명을 입력하세요")}
            disabled={disabled}
            required={required}
            className={`w-full ${!isSearchOnly ? 'pl-28' : 'px-3'} pr-20 py-1.5 border border-gray-300 rounded-3xl focus:outline-none focus:ring-1 focus:ring-blue-500 ${
              !validateInput() ? 'border-red-500' : 'border-gray-300'
            } ${disabled ? 'bg-gray-100' : ''} ${className}`}
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {inputValue && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
            <Search className="w-4 h-4 text-gray-400" />
          </div>
        </div>

        {!isSearchOnly && inputType === 'school' && !validateInput() && inputValue && (
          <p className="text-red-500 text-sm mt-1">
            유효한 학교명을 선택해주세요.
          </p>
        )}

        {showSuggestions && inputType === 'school' && createPortal(
          <div 
            ref={dropdownRef}
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              zIndex: 9999999
            }}
          >
            {filteredSchools.map((school, index) => {
              const abbreviation = SchoolNameUtils.getAbbreviation(school.school_name, school.school_level);
              return (
                <div
                  key={`${school.school_name}-${index}`}
                  onClick={() => handleSchoolSelect(school)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                    selectedIndex === index ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">
                      {school.school_name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {abbreviation}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {school.address || '주소 정보 없음'}
                  </div>
                  {/* <div className="text-xs text-gray-500 mt-1">
                    {school.school_level}
                  </div> */}
                </div>
              );
            })}
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default SchoolSearchInput;